import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Search, Plus, Minus } from 'lucide-react';

export interface FAQ {
    question: string;
    answer: string;
}

export const faqData: FAQ[] = [
    {
        question: "What goes in the video or ppt question in the form above?",
        answer: " A video (upto 10 minutes) or PPT (upto 15 slides) is to help us understand you better. It can be used to express about yourself and what makes you want to join us. You can also add in your journey as an MHP and the values that you hold, along with your relevant skills for the role and how they came about. \n If uploading a video, please make sure it's recorded in a well-lit area with as little background noise as possible, so that we can understand you well."
    },
    {
        question: "What is the duration requirement for the video?",
        answer: "The video should be a maximum of 10 minutes. There is no specific minimum duration requirement."
    },
    {
        question: "Is the invitation open to individuals who are not certified counselors?",
        answer: "Yes, if you are in your final year of a master's program, you can apply for the Associate counselor position. If you are not, you're welcome to propose a position based on your vision of how you'd like to work with us!"
    },
    {
        question: "Can I combine both a PowerPoint presentation and video/audio?",
        answer: "Yes, you can combine a PowerPoint presentation with video/audio. The overall limit will remain similar, but you are encouraged to express yourself in a concise yet comprehensive manner. The stated caps should be taken as a general guideline rather than a strict rule."
    },
    {
        question: "If I become a part of Group Practice, will I be able to switch to Private Practice?",
        answer: "The tenure of each therapist is a year, post which you get an option to continue in the same role, promote to a senior role, or exit the practice and stay connected as an alumni or peer. The process will be amicable and mutually decided upon."
    },
    {
        question: "Can expressive arts therapists apply?",
        answer: "Yes! We're always hoping for more diversity."
    },
    {
        question: "How many people would be there in the practice?",
        answer: "We're evolving the numbers as we go, roughly 4 teams with 4-5 therapists each is the estimate right now."
    },
    {
        question: "How would client assignments or referrals work?",
        answer: "Clients coming through the organization portal will be assigned by a specific non-therapy mental health personnel who will match clients with therapists based on certain logistical measures, with preference given to those whose case loads are lighter."
    },
    {
        question: "How would the supervision be provided individually or in groups?",
        answer: "Both! 2 individual meetings, and 1 group meeting a month has been planned up until now, though some supervisors prefer 2 group and minimum 1 individual supervision meets."
    },
    {
        question: "What is the revenue sharing model?",
        answer: "There is no revenue-sharing - you keep the entire amount that you get from clients, and pay for the services that you avail from the practice."
    },


    {
        question: "Is the monthly fee for supervision and training required even if I don't currently have clients?",
        answer: "Just like in private practice, in the exceptional event of being low on clients once you've joined the practice, you can discuss the situation with your supervisor and decide on the best course of action, based on if and how they accommodate your request."
    },
    {
        question: "Can I access only specific parts of the subscription rather than the entire package?",
        answer: "Separate services are readily available. However, Associate therapists who wish to access all the services in the package (supervision, discussing and reflecting on increasing visibility as a therapist, and monthly upskilling) will be prioritized for inclusion in the group practice."
    },
    {
        question: "Could the supervision fees be tiered based on affordability or income?",
        answer: "The fees will be fixed, as this is the standard practice in most supervision and upskilling spaces. Some supervisors offer a sliding scale, pay-as-you-can, or a fee according to your fee towards your clients - this will depend on the discretion of the supervisor."
    },
    {
        question: "Is it mandatory to go for upskilling and supervision every month?",
        answer: "For Associate therapists, it will be mandatory to join in these. They constitute a total of 5 meetings a month (including 2 individual supervision, 1 group supervision, 1 upskilling workshop, and 1 increasing visibility meet) constituting a total of 7 hours, required to ensure quality learning and care for clients. Exceptions can be made on a case-to-case basis."
    },
    {
        question: "Is there going to be any contract for a year?",
        answer: "There will be a contract, and you can opt out by giving a notice of 30 days so that the space can accommodate the next person on the waiting list."
    },
    {
        question: "Can I see the list of supervisors before I join?",
        answer: "Supervisors are still being added to the roster. You won't just be assigned a supervisor; a mutual match will be undertaken. We assure you that the supervisors onboarded align with the same values and vision of our model."
    },
    {
        question: "I saw that one more point was added, client acquisition. Can you please explain this one?",
        answer: "Client acquisition is getting clients to start your roster. In traditional private practice space, it's a primary goal as it is the main source of income. While it usually takes a few days to weeks to bring your roster to a sufficient number, we are looking at collaborations with subscription-based models that can help you acquire clients faster. This will be optional and incur an additional small fee, details of which will be provided once the space accepts new therapists."
    },
    {
        question: "As someone starting out, I am currently struggling to establish a consistent client base. Could you let me know if there would be any support in terms of client referrals or guidance on building a practice within this collaborative setup?",
        answer: `There are four ways we'll be supporting you: \
* Referrals within the community, as you connect with more peers during monthly team-wide meetings 
* All seniors and senior-tracks are private practitioners who have set up their practice, from whom you can directly learn how to do it all 
* The website portal is being optimised for search-engines and hopefully will be helpful in receiving client enquiries   
* We'll have monthly meetings on how you can create visibility in order to be seen and eventually, have clients come in as they see more of you.`
    },
    {
        question: "What's the difference between being a associate psychologist at your place than just taking supervision from outside? Is there a difference? ",
        answer: "In addition to supervision as you can receive from independent practitioners, this space adds layers of support in terms of upskilling, referrals, entrepreneurship, and overall help in coping with loneliness that comes with being a private practitioner."
    }
];



interface FAQSectionProps {
    className?: string;
    customFaqs?: FAQ[];
}

const FAQSection: React.FC<FAQSectionProps> = ({ className = "", customFaqs }) => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [displayCount, setDisplayCount] = useState(5);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = useState<number | "auto">("auto");

    const faqs = customFaqs || faqData;

    // Filter FAQs based on search term
    const filteredFaqs = faqs.filter(faq =>
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const displayedFaqs = filteredFaqs.slice(0, displayCount);

    useEffect(() => {
        const updateHeight = () => {
            if (containerRef.current) {
                const height = containerRef.current.scrollHeight;
                setContainerHeight(height);
            }
        };

        // Update height after content changes
        requestAnimationFrame(() => {
            updateHeight();
        });

        // Update height when an FAQ is toggled
        if (openIndex !== null) {
            const timer = setTimeout(updateHeight, 200);
            return () => clearTimeout(timer);
        }
    }, [displayCount, openIndex, searchTerm]);

    const toggleFaq = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const handleLoadMore = () => {
        if (containerRef.current) {
            // First set the container to its current height explicitly
            setContainerHeight(containerRef.current.scrollHeight);

            // Then update the display count after a frame
            requestAnimationFrame(() => {
                setDisplayCount(prev => prev + 5);
            });
        }
    };

    return (
        <section className={`w-full py-12 ${className}`} id=''>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-10">
                    <h2 className="text-3xl md:text-4xl font-bold text-teal-800 mb-4 font-fraunces-slab">
                        Frequently Asked Questions
                    </h2>
                    <p className="text-teal-600 max-w-2xl mx-auto">
                        Find answers to common questions about our practice, process, and services.
                    </p>
                </div>

                {/* Search bar */}
                <div className="relative mb-8 max-w-md mx-auto">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-teal-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white shadow-sm"
                        placeholder="Search questions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {filteredFaqs.length === 0 ? (
                    <div className="text-center py-10">
                        <p className="text-gray-500">No matching questions found. Try a different search term.</p>
                    </div>
                ) : (
                    <>
                        <motion.div
                            ref={containerRef}
                            style={{ height: typeof containerHeight === "number" ? containerHeight + "px" : containerHeight }}
                            className="space-y-4 overflow-hidden"
                            initial={false}
                            animate={{ height: typeof containerHeight === "number" ? containerHeight + "px" : containerHeight }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                        >
                            {displayedFaqs.map((faq, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="bg-white rounded-lg shadow-sm overflow-hidden border-l-4 border-teal-500 hover:shadow-md transition-shadow duration-300"
                                >
                                    <button
                                        className="w-full px-6 py-4 flex justify-between items-center text-left focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-inset"
                                        onClick={() => toggleFaq(index)}
                                        aria-expanded={openIndex === index}
                                        aria-controls={`faq-answer-${index}`}
                                    >
                                        <span className="font-medium text-gray-900 text-lg pr-4">
                                            {faq.question}
                                        </span>
                                        <motion.div
                                            className="flex-shrink-0 bg-teal-100 rounded-full p-2"
                                            initial={false}
                                            animate={{ rotate: openIndex === index ? 180 : 0 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            {openIndex === index ? (
                                                <Minus className="w-4 h-4 text-teal-700" />
                                            ) : (
                                                <Plus className="w-4 h-4 text-teal-700" />
                                            )}
                                        </motion.div>
                                    </button>
                                    <AnimatePresence>
                                        {openIndex === index && (
                                            <motion.div
                                                id={`faq-answer-${index}`}
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <div className="px-6 py-5 text-gray-700 border-t border-gray-100 bg-gray-50">
                                                    <div className="prose max-w-none">
                                                        {faq.answer.split('\n').map((paragraph, i) => (
                                                            <p key={i} className="mb-3 last:mb-0">
                                                                {paragraph}
                                                            </p>
                                                        ))}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            ))}
                        </motion.div>

                        {displayCount < filteredFaqs.length && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="flex justify-center mt-8"
                            >
                                <button
                                    onClick={handleLoadMore}
                                    className="px-6 py-3 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 shadow-sm flex items-center"
                                >
                                    <span>Show More Questions</span>
                                    <ChevronDown className="ml-2 w-4 h-4" />
                                </button>
                            </motion.div>
                        )}
                    </>
                )}

                {/* Support contact */}
                <div className="mt-16 text-center">
                    <p className="text-gray-600">
                        Still have questions? We're here to help.
                    </p>
                    <a
                        href="mailto:grehpraveshtherapy@gmail.com"
                        className="inline-block mt-2 text-teal-600 hover:text-teal-800 font-medium underline"
                    >
                        Contact our support team
                    </a>
                </div>
            </div>
        </section>
    );
};

export default FAQSection;